/**
 * Plant Manager surface — fleet intelligence and economics context.
 *
 * Quality rate is reported because inspection results support it. Availability,
 * performance and OEE remain explicitly unavailable until EUROMAP 77 telemetry
 * is connected — never assumed 100%.
 */
import { useState } from 'react';
import { api, qs } from '../api/client';
import { useApi } from '../hooks/useApi';
import type { FleetOverview, OeeRow, RateMetrics } from '../api/types';
import { Card, Empty, ErrorBox, Loading, Metric, StatePill, Unavailable } from '../components/Metric';
import { ShareBar } from '../components/charts';

const WINDOWS = [
  { h: 8, label: '8h' },
  { h: 24, label: '1d' },
  { h: 72, label: '3d' },
  { h: 168, label: '7d' },
];

export function PlantPage() {
  const [hours, setHours] = useState(24);
  const from = new Date(Date.now() - hours * 3600_000).toISOString();
  const to = new Date().toISOString();

  const fleet = useApi<FleetOverview>(() => api.get<FleetOverview>('/api/v1/fleet/overview'), []);
  const rates = useApi<{ rates: RateMetrics }>(
    () => api.get(`/api/v1/metrics/rates${qs({ from, to })}`),
    [hours],
  );
  const oee = useApi<{
    machines: OeeRow[];
    availabilityAndPerformance: null;
    reason: string;
  }>(() => api.get(`/api/v1/metrics/oee${qs({ from, to })}`), [hours]);

  if (fleet.error) return <ErrorBox message={fleet.error} />;
  if (fleet.loading) return <Loading label="Loading fleet…" />;

  const t = fleet.data?.totals;
  const r = rates.data?.rates;

  return (
    <>
      <div className="btn-row" style={{ marginBottom: 16 }}>
        <div className="seg" role="group" aria-label="Time window">
          {WINDOWS.map((w) => (
            <button key={w.h} className={hours === w.h ? 'on' : ''} onClick={() => setHours(w.h)}>
              {w.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-4">
        <Metric label="Sites" value={t?.sites ?? 0} from="Registered in this organisation" />
        <Metric
          label="Machines"
          value={t?.machines ?? 0}
          sub="The billable unit. Cameras are not billed."
          from="Licensed per machine"
        />
        <Metric label="Camera stations" value={t?.stations ?? 0} from="Onboarded on machines" />
        <Metric
          label="Cycles inspected"
          value={r?.totalEvents ?? 0}
          sub={`${r?.labelledEvents ?? 0} labelled by humans`}
          from={`Window: last ${hours}h`}
        />
      </div>

      <div className="grid grid-2" style={{ marginTop: 14 }}>
        <Card title="Fleet state">
          {t ? (
            <ShareBar
              parts={[
                { label: 'Active', value: t.byState.ACTIVE ?? 0, color: 'var(--ok)' },
                { label: 'Shadow', value: t.byState.SHADOW ?? 0, color: 'var(--brand)' },
                { label: 'Passive', value: t.byState.PASSIVE ?? 0, color: 'var(--border-strong)' },
              ]}
            />
          ) : (
            <Empty title="No fleet data" />
          )}
        </Card>
        <div className="grid grid-2" style={{ gap: 14 }}>
          <Metric
            label="Escape rate (fleet)"
            tone={r?.escapeRatePct == null ? 'default' : r.escapeRatePct > 2 ? 'danger' : r.escapeRatePct > 0.5 ? 'warn' : 'ok'}
            value={r?.escapeRatePct != null ? r.escapeRatePct.toFixed(2) : undefined}
            unit={r?.escapeRatePct != null ? '%' : undefined}
            sub="Real defects passed as good"
          >
            <Unavailable reason="No labelled cycles in this window." />
          </Metric>
          <Metric
            label="False-reject rate (fleet)"
            value={r?.falseRejectRatePct != null ? r.falseRejectRatePct.toFixed(2) : undefined}
            unit={r?.falseRejectRatePct != null ? '%' : undefined}
            sub="Good parts flagged"
          >
            <Unavailable reason="No labelled cycles in this window." />
          </Metric>
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <Card
          title="Per-machine quality"
          note={
            oee.data?.reason ??
            'Quality rate is derived from inspection results. Availability and performance require EUROMAP 77 telemetry and are shown as unavailable until it is connected.'
          }
        >
          {oee.error && <ErrorBox message={oee.error} />}
          {oee.loading && <Loading />}
          {!oee.data || oee.data.machines.length === 0 ? (
            <Empty title="No machines in this window" />
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Machine</th>
                    <th className="num">Cycles</th>
                    <th className="num">Good</th>
                    <th className="num">Defect</th>
                    <th className="num">Quality rate</th>
                    <th className="num">Availability</th>
                    <th className="num">Performance</th>
                    <th className="num">OEE</th>
                  </tr>
                </thead>
                <tbody>
                  {oee.data.machines.map((m) => (
                    <tr key={m.machineId}>
                      <td style={{ fontWeight: 600 }}>{m.machineName}</td>
                      <td className="num">{m.totalCycles}</td>
                      <td className="num">{m.goodCycles}</td>
                      <td className="num">{m.defectCycles}</td>
                      <td className="num">
                        {m.qualityRatePct != null ? `${m.qualityRatePct.toFixed(1)}%` : '—'}
                      </td>
                      <td className="num" style={{ color: 'var(--muted)', fontStyle: 'italic' }}>n/a</td>
                      <td className="num" style={{ color: 'var(--muted)', fontStyle: 'italic' }}>n/a</td>
                      <td className="num" style={{ color: 'var(--muted)', fontStyle: 'italic' }}>n/a</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <div style={{ marginTop: 14 }}>
        <Card title="Rollout status by site">
          {!fleet.data || fleet.data.sites.length === 0 ? (
            <Empty title="No sites registered" />
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Site</th>
                    <th>Machine</th>
                    <th>Station</th>
                    <th>State</th>
                    <th className="num">Baseline</th>
                  </tr>
                </thead>
                <tbody>
                  {fleet.data.sites.flatMap((s) =>
                    s.machines.flatMap((m) =>
                      m.cameraStations.length === 0
                        ? [
                            <tr key={m.id}>
                              <td>{s.name}</td>
                              <td>{m.name}</td>
                              <td colSpan={3} style={{ color: 'var(--ink-3)' }}>
                                No camera station onboarded
                              </td>
                            </tr>,
                          ]
                        : m.cameraStations.map((st) => (
                            <tr key={st.id}>
                              <td>{s.name}</td>
                              <td>{m.name}</td>
                              <td style={{ fontWeight: 600 }}>{st.name}</td>
                              <td>
                                <StatePill state={st.state} />
                              </td>
                              <td className="num">
                                {st.baselineImageCount}
                                {st.baselineReady ? '' : ' (incomplete)'}
                              </td>
                            </tr>
                          )),
                    ),
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
